import { forwardRef, useEffect, useImperativeHandle } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { Form } from '@/components/ui/form';
import { FeatureMode, FeatureTabRef } from '@/modules/common';

import {
  FORM_FIELD_ALLOCATION_FORMAT,
  FORM_FIELD_ALLOW_CARRYOVER,
  FORM_FIELD_CARRYOVER_DAYS,
} from '../../../constants/time-off-type-form-fields';
import {
  TimeOffAllocationFormat,
  TimeOffTypeCode,
  TimeOffTypeDetails,
} from '../../../types/time-off';
import {
  TimeOffTypeSetupFormData,
  timeOffTypeSetupSchema,
} from '../../../validations/time-off-type-validation';
import { AllocationSettingsSection } from './sections/allocation-settings-section';
import { ApprovalRulesSection } from './sections/approval-rules-section';
import { BalanceSection } from './sections/balance-section';
import { CarryoverRulesSection } from './sections/carryover-rules-section';
import { CreditOnCreationSection } from './sections/credit-on-creation-section';
import { DocumentRequirementSection } from './sections/document-requirement-section';
import { MemberCapacitySection } from './sections/member-capacity-section';
import { PartialDayOptionsSection } from './sections/partial-day-options-section';
import { ProrateAccrualSection } from './sections/prorate-accrual-section';

interface TimeOffSetupTabProps {
  mode: FeatureMode;
  initialData?: TimeOffTypeDetails | null;
  resetKey?: number;
  onFormStateChange?: (isDirty: boolean, hasChanges?: boolean, isValid?: boolean) => void;
}

const buildDefaultValues = (data?: TimeOffTypeDetails | null): TimeOffTypeSetupFormData => ({
  allocationFormat: data?.format ?? TimeOffAllocationFormat.Yearly,
  allocationDays: data?.allocationDays ?? 0,
  dateOfRenewal: data?.dateOfRenewal ?? { month: 1, day: 1 },
  allowCarryover: data?.allowCarryover ?? false,
  carryoverDays: data?.carryoverDays ?? null,
  allowNegativeBalance: data?.allowNegativeBalance ?? false,
  negativeBalanceDays: data?.negativeBalanceDays ?? null,
  hasMaxLimit: data?.hasMaxLimit ?? false,
  maxLimitDays: data?.maxLimitDays ?? null,
  isApprovalRequired: data?.isApprovalRequired ?? false,
  requireFileUpload: data?.requireFileUpload ?? false,
  allowHalfDay: data?.allowHalfDay ?? false,
  allowQuarterDay: data?.allowQuarterDay ?? false,
  allowHourlyRequests: data?.allowHourlyRequests ?? false,
  considerMemberCapacity: data?.considerMemberCapacity ?? false,
  creditOnCreation: data?.creditOnCreation ?? false,
  prorateAccrual: data?.prorateAccrual ?? true,
});

export const TimeOffSetupTab = forwardRef<
  FeatureTabRef<TimeOffTypeSetupFormData>,
  TimeOffSetupTabProps
>(({ mode, initialData, resetKey, onFormStateChange }, ref) => {
  const isReadMode = mode === FeatureMode.Read;

  const form = useForm<TimeOffTypeSetupFormData>({
    resolver: zodResolver(timeOffTypeSetupSchema),
    defaultValues: buildDefaultValues(initialData),
    mode: 'onChange',
  });

  useEffect(() => {
    if (initialData) {
      form.reset(buildDefaultValues(initialData));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, resetKey]);

  // Report form state to parent on every change
  const { isDirty, isValid } = form.formState;
  useEffect(() => {
    onFormStateChange?.(isDirty, isDirty, isValid);
  }, [isDirty, isValid, onFormStateChange]);

  useImperativeHandle(ref, () => ({
    submit: async () => {},
    isValid,
    getFormData: () => form.getValues(),
  }));

  const showDocumentRequirement = initialData?.code === TimeOffTypeCode.SICK_LEAVE;
  const initialFormat = initialData?.format ?? TimeOffAllocationFormat.Yearly;
  const allocationFormat = form.watch(FORM_FIELD_ALLOCATION_FORMAT);
  const shouldShowCarryoverSection = allocationFormat === TimeOffAllocationFormat.Yearly;

  useEffect(() => {
    if (isReadMode || shouldShowCarryoverSection) return;

    if (form.getValues(FORM_FIELD_ALLOW_CARRYOVER)) {
      form.setValue(FORM_FIELD_ALLOW_CARRYOVER, false, { shouldDirty: true, shouldValidate: true });
    }

    if (form.getValues(FORM_FIELD_CARRYOVER_DAYS) != null) {
      form.setValue(FORM_FIELD_CARRYOVER_DAYS, null, { shouldDirty: true, shouldValidate: true });
    }
  }, [form, isReadMode, shouldShowCarryoverSection]);

  // Force watch so RHF re-renders on field changes (needed for isDirty to update)
  form.watch();

  return (
    <Form {...form}>
      <form className="mb-4 space-y-4">
        <AllocationSettingsSection
          form={form}
          isReadMode={isReadMode}
          initialFormat={initialFormat}
        />

        <CreditOnCreationSection form={form} isReadMode={isReadMode} />

        <ProrateAccrualSection form={form} isReadMode={isReadMode} />

        {shouldShowCarryoverSection && (
          <CarryoverRulesSection form={form} isReadMode={isReadMode} />
        )}

        <BalanceSection form={form} isReadMode={isReadMode} />

        <ApprovalRulesSection form={form} isReadMode={isReadMode} />

        {showDocumentRequirement && (
          <DocumentRequirementSection form={form} isReadMode={isReadMode} />
        )}

        <PartialDayOptionsSection form={form} isReadMode={isReadMode} />

        <MemberCapacitySection form={form} isReadMode={isReadMode} />
      </form>
    </Form>
  );
});

TimeOffSetupTab.displayName = 'TimeOffSetupTab';
